import { ApolloError, useMutation } from '@apollo/client'
import { MUTATION_CREATE_FORM_MODEL } from '@libs/apollo/mutations'
import { FORM_MODELS } from '@libs/apollo/queries'

import {
  FieldType,
  FormFieldInput,
  FormModel,
  FormModelCreateInput,
  LocalFile,
  MutationCreateFormModelArgs,
} from '@types'
import { useCreateFile } from '../file/create-file.hook'

interface Data {
  createFormModel?: FormModel
}

export const useCreateFormModel = (): {
  createFormModel: (input: FormModelCreateInput, localFiles: Record<string, LocalFile[]>) => void
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const [createFormModel, { loading, error, data }] = useMutation<Data, MutationCreateFormModelArgs>(
    MUTATION_CREATE_FORM_MODEL,
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const { createFile } = useCreateFile()

  const _uploadFiles = async (localFiles: Record<string, LocalFile[]> = {}): Promise<Record<string, string[]>> => {
    const uploadedFiles: Record<string, string[]> = {}

    for (const [fieldId, files] of Object.entries(localFiles)) {
      const fileIds: string[] = []
      for (const file of files) {
        const result = await createFile(file)
        if (result?.id) fileIds.push(result.id)
      }
      if (fileIds.length > 0) {
        uploadedFiles[fieldId] = fileIds
      }
    }

    return uploadedFiles
  }

  const _updateFieldsWithFiles = (fields: FormFieldInput[], uploadedFiles: Record<string, string[]>) => {
    return fields.map((field) => {
      if (field.type === FieldType.FixedFile || field.type === FieldType.FixedPicture) {
        const fileField = field.type === FieldType.FixedFile ? field.fixed_file : field.fixed_picture
        if (fileField && uploadedFiles[fileField.id]) {
          return {
            ...field,
            [field.type === FieldType.FixedFile ? 'fixed_file' : 'fixed_picture']: {
              ...fileField,
              ids: [...(fileField.ids || []), ...uploadedFiles[fileField.id]],
            },
          }
        }
      }

      const headerPictureField = field.type === FieldType.HeaderPicture ? field.header_picture : undefined
      if (headerPictureField && uploadedFiles[headerPictureField.id]) {
        return {
          ...field,
          ['header_picture']: {
            ...headerPictureField,
            fileId: uploadedFiles[headerPictureField.id][0],
          },
        }
      }

      return field
    })
  }

  const _createFormModel = async (input: FormModelCreateInput, localFiles: Record<string, LocalFile[]> = {}) => {
    // Upload all local files first
    const uploadedFiles = await _uploadFiles(localFiles)

    const processedInput = structuredClone(input)

    // Update fields with new file IDs
    processedInput.structure.fields = _updateFieldsWithFiles(input.structure.fields, uploadedFiles)

    try {
      await createFormModel({
        variables: { input: processedInput },
        refetchQueries: [
          {
            query: FORM_MODELS,
            variables: {
              projectId: input.projectId,
            },
          },
        ],
      })
    } catch (error) {
      console.error(error)
    }
  }

  return { createFormModel: _createFormModel, loading, error, data }
}
